import React, {FunctionComponent, useEffect, useState} from "react";
import {
    Button,
    Grid
} from "@mui/material";

import {useSnackbar} from "notistack";
import GenerationEnergyChart from "./components/GenerationEnergyChart";
import CardList from "./components/CardList";
import { FindTeleObjectInverterReactQuery, FindNominalPowerByEquipmentIdReactQuery} from "./InverterReactQuery";
import InverterNodes from "./components/InverterNodes";
import {truncateNumber} from "../../../../Utils/NumberUtil";
import PowerIrradianceInverterChart from "./components/PowerIrradianceInverterChart";
import EquipmentGrid from "./EquipmentGrid/EquipmentGrid";
import StringComponent from "./components/StringComponent";
import InverterCommandSelector from "./components/InverterCommandSelector";
import OfflineBoltOutlinedIcon from "@mui/icons-material/OfflineBoltOutlined";
import {SystemRoutes} from "../../../../Utils/RouteUtils";
import { ASectionTitle } from "@atiautomacao/ati-ui-library";
import {useNavigate} from "react-router-dom";
import {NavigationEquipment, NavigationPowerStation, NavigationSkid} from "../../../../Shared/Types/Navigation";
import {West} from "@mui/icons-material";

export interface EquipmentProps {
    id: number;
    name: string;
    equipmentType:any
}
export interface SkidProps {
    id: number;
    name: string;
    equipmentList: EquipmentProps[];
}
const InvertersPage: FunctionComponent<{powerPlant: NavigationPowerStation, equipment:NavigationEquipment, skid: NavigationSkid}> = ({powerPlant, equipment, skid}) => {
    const navigate = useNavigate();
    const selectedId = equipment ? equipment.id : -1;
    const mutate = FindTeleObjectInverterReactQuery();
    const nominalPowerQuery = FindNominalPowerByEquipmentIdReactQuery(selectedId);
    const [powerDataList,setPowerDataList]  = useState<any[]>([]);
    const [sideDCDataList,setSideDCDataList]  = useState<any[]>([]);
    const [sideACDataList,setSideACDataList]  = useState<any[]>([]);
    const [operatingDataList,setOperatingDataList]  = useState<any[]>([]);
    const [energyDataList,setEnergyDataList]  = useState<any[]>([]);
    const [solarimetricStationDataList,setSolarimetricStationDataList]  = useState<any[]>([]);
    const [statusDataList,setStatusDataList]  = useState<any[]>([]);
    const [nodeWithElements,setNodeWithElements]  = useState<any>();
    const [nominalPower,setNominalPower]  = useState<any>(null);

    useEffect(() => {        
        if (selectedId !== -1 && nominalPowerQuery.data?.data?.nominalPowerValue != undefined) {
            setNominalPower(nominalPowerQuery.data?.data.nominalPowerValue || null);
        }

    }, [selectedId,nominalPowerQuery]);

    useEffect(() => {
        if(selectedId !== -1 && powerPlant.id){
            mutate.mutate({equipmentId: selectedId, powerStationId: powerPlant.id},
                {
                    onSuccess: (data) => {
                        setPowerDataList(data?.data?.powerDataList || []);
                        setSideDCDataList(data?.data?.sideDCDataList || []);
                        setSideACDataList(data?.data?.sideACDataList || []);
                        setOperatingDataList(data?.data?.operatingDataList || []);
                        setEnergyDataList(data?.data?.energyDataList || []);
                        setStatusDataList(data?.data?.statusDataList || []);
                        setSolarimetricStationDataList(data?.data?.solarimetricStationDataList || []);
                        setNodeWithElements(data?.data?.nodeWithElements || {});
                    },
                    onError: (error: any) => {
                        enqueueSnackbar(`Erro ao carregar dados: ${error.message}`, { variant: "error" });
                    }
                });
        } else {
            setPowerDataList([]);
            setSideDCDataList([]);
            setSideACDataList([]);
            setOperatingDataList([]);
            setEnergyDataList([]);
            setStatusDataList([]);
            setSolarimetricStationDataList([]);
            setNodeWithElements({});
        }

    }, [powerPlant.id, selectedId]);

    const { enqueueSnackbar } = useSnackbar()
    return (
        <Grid container>
            <Grid item xs={12} spacing={2} style={{marginRight: '20px'}}>
                <ASectionTitle
                    styleContainer={{ padding: '0 0 18px 0' }}
                    styleContainerTitle={{ padding: '0px' }}
                    styleTitle={{ fontWeight: 700, fontSize: 18 }}
                    titleIcon={
                        <OfflineBoltOutlinedIcon
                            sx={{
                                marginRight: 1,
                                color: '#171717',
                                width: '29.11px',
                                height: '29.11px',
                                opacity: 1,
                            }}/>
                    }
                    titleLabel={
                        equipment?.name
                            ? skid?.name
                                ? `${equipment.name} | ${skid.name}`
                                : equipment.name
                            : ""
                    }
                    contentChildrenStyle={{ alignItems: 'center' }}
                    contentChildren={
                        <Button
                            variant="text"
                            color="primary"
                            size="small"
                            startIcon={<West />}
                            onClick={() => {navigate(SystemRoutes.DASHBOARD_POWER_PLANT_INVERTER_SIMPLE)}}
                            sx={{
                                textTransform: 'uppercase',
                                '&:hover': {
                                    backgroundColor: 'transparent'
                                },
                                display: 'flex',
                                justifyContent: 'right',
                            }}
                        >
                            Voltar para a visão geral do inversor
                        </Button>
                    }
                />
            </Grid>
            <Grid container xs={12} spacing={2}>
                <Grid item md={3} xs={12}>
                    <InverterCommandSelector equipmentId={selectedId} />
                    <CardList ariaLabel={"dashboard-station-inverter-dcside"} height={248} data={sideDCDataList} label="Lado DC"  isSuccess={mutate.isSuccess}/>
                    <CardList ariaLabel={"dashboard-station-inverter-acside"} height={449} data={sideACDataList} label="Lado AC"  isSuccess={mutate.isSuccess}/>
                </Grid>
                <Grid item md={6} xs={12} >
                    <InverterNodes
                        statusData={statusDataList}
                        solarimetricStation={solarimetricStationDataList}
                        entities={nodeWithElements}
                        isSuccess={mutate.isSuccess}
                        title={equipment?.name ?? ""}
                    />
                </Grid>
                <Grid item md={3} xs={12}>
                    <CardList ariaLabel={"dashboard-station-inverter-power-data"} height={truncateNumber(280)} data={powerDataList} label="Dados de Potência" isSuccess={mutate.isSuccess}/>
                    <CardList ariaLabel={"dashboard-station-inverter-energy-data"} height={truncateNumber(163)} data={energyDataList} label="Dados de Energia" isSuccess={mutate.isSuccess}/>
                    <CardList ariaLabel={"dashboard-station-inverter-operation-data"} height={truncateNumber(239)} data={operatingDataList} label="Dados de Funcionamento" isSuccess={mutate.isSuccess}/>
                </Grid>
            </Grid>

            <Grid container xs={12} spacing={2}>
                <Grid item xs={12} sx={{pb: 2}}>
                    <StringComponent equipmentId={selectedId} />
                </Grid>
            </Grid>
            <Grid container xs={12} spacing={2}>
                <Grid item xs={12} md={6}>
                    <GenerationEnergyChart powerPlantId={powerPlant.id} equipmentId={selectedId}/>
                </Grid>
                <br/>
                <Grid item xs={12} md={6}>
                    <PowerIrradianceInverterChart powerPlant={powerPlant} equipmentId={selectedId} nominalPower={nominalPower}/>
                </Grid>
            </Grid>
            <Grid container xs={12} spacing={2}>
                <Grid item xs={12}>
                    <EquipmentGrid equipmentId={selectedId} />
                </Grid>
            </Grid>
        </Grid>
    )
}

export default InvertersPage;
